/**
 * Full, bake-from-it recipes — the ones with real weights and real steps.
 *
 * Separate from recipes.json (which is the card catalog) so a card can exist
 * without a full recipe behind it. RecipeModal looks an id up here; if there
 * is nothing, it falls back to the "kitchen notes on request" block.
 *
 * ── Deep Fudge Brownies ──────────────────────────────────────────────────
 * Source of truth: docs/truenooch-brownies-v3-as-built.md plus the "TrueNooch
 * Brownie v3" as-built artifact (2026-09-09). This is the FIRST real recipe
 * TrueNooch has published — every other modal still says "coming soon".
 *
 * GRAMS ONLY, no cups — Jarvis's call, 2026-09-12. The cup measures were his
 * at the bench; the gram conversions were Claude's and unweighed, so publishing
 * both would imply a precision the volumes do not have. He has now baked it
 * four times and ruled the weights good.
 *
 * Bake time is a RANGE with doneness cues, not a number, and the range is
 * explained rather than hedged (his 2026-09-12 note): glass in a conventional
 * oven ran 89 minutes; thin metal in convection lands nearer 45. His own
 * schedule — first fork test at 35, then every 10–15 — replaces the "check
 * every 3" that was in the earlier draft and was wrong (20 door openings on a
 * long bake, 25–50 °F each).
 *
 * ⚑ ALLERGEN: the Forager yogurt is cashew-and-coconut. Cashew is one of the
 * nine majors, so this recipe carries its own CONTAINS line. The site's
 * Nut-Free / Tree-nut-free chips are claims about the ROUND and do not travel
 * to a brownie with cashew in it (as-built artifact, "for the label reissue").
 *
 * Tier S: how the flour is made — lot, dryer, temperature, mill, yield — is
 * deliberately absent. The flour enters as a weight only.
 */

export interface RecipeIngredient {
  amount: string;
  item: string;
  note?: string;
}

export interface RecipeStep {
  heading: string;
  body: string[];
}

export interface RecipeStat {
  label: string;
  base: string;
  withFlour: string;
  delta: string;
}

export interface FullRecipe {
  id: string;
  name: string;
  yieldLine: string;
  intro: string[];
  contains?: string;
  groups: { label: string; items: RecipeIngredient[] }[];
  steps: RecipeStep[];
  stats?: { label: string; caption: string; rows: RecipeStat[]; footnote: string };
  storage: string;
}

const FLOUR_BROWNIES: FullRecipe = {
  id: 'flour-brownies',
  name: 'Deep Fudge Brownies',
  yieldLine: 'One 9×11 pan · 16 brownies · 350 °F',
  intro: [
    'A brownie is one of the few sweets that wants a savory ingredient in it. That is what the salt is doing, and the coffee, and it is why dark chocolate reads better in a brownie than milk chocolate does. TrueNooch™ Flour walks straight into that gap — on its own it tastes deeply savory, and folded into cocoa and coconut sugar it stops tasting like anything you can name and starts making the chocolate taste more like chocolate.',
    'There are no eggs and no dairy in this pan. Cashew-and-coconut yogurt does the binding the eggs used to do, and it does it better than we expected — the slab holds its edges and cuts clean. The flour runs one-to-one against the gluten-free flour, which is the ratio where it stops being a garnish and starts being an ingredient.',
    'And the sugar: this pan runs about a third of what a brownie this size normally takes. We did not plan that — it came out of the bench that way and it was good, so it stayed. If you want it sweeter, add sugar; you will not hurt anything.',
  ],
  contains: 'Contains tree nuts (cashew). No eggs, no dairy.',
  groups: [
    {
      label: 'Bloom first',
      items: [
        { amount: '150 g', item: 'TrueNooch™ Flour' },
        { amount: '120 g', item: 'hot water', note: 'for the bloom — see step one' },
      ],
    },
    {
      label: 'Dry',
      items: [
        { amount: '150 g', item: 'gluten-free all-purpose flour' },
        { amount: '6 g', item: 'baking powder', note: 'whisked into the flour, never into the wet' },
        {
          amount: '2 g',
          item: 'fine sea salt',
          note: 'TrueNooch™ Flour already carries sea salt; this is the balance, not the seasoning',
        },
      ],
    },
    {
      label: 'Wet',
      items: [
        { amount: '368 g', item: 'cashew-and-coconut yogurt', note: 'unsweetened; this is the binder' },
        { amount: '155 g', item: 'coconut sugar' },
        { amount: '90 g', item: 'Dutch-process cocoa', note: 'we use a double-dark blend' },
        { amount: '120 g', item: 'hot water', note: 'for the cocoa — or hot coffee, if you want it' },
        { amount: '107 g', item: 'avocado oil' },
        { amount: '25 g', item: 'vanilla extract', note: 'yes, that much — it earns it' },
      ],
    },
    { label: 'Last', items: [{ amount: '255 g', item: 'dairy-free chocolate chunks' }] },
  ],
  steps: [
    {
      heading: 'Bloom the flour — this is the whole recipe',
      body: [
        'Stir the 150 g of TrueNooch™ Flour into the 120 g of hot water and leave it alone for 15 minutes, before anything else touches it. It will go from loose to thick and spoonable as it drinks.',
        'Skip this and the flour takes its water out of the rest of the batter instead, and you taste it — a faint sandiness on the tongue that no amount of extra mixing fixes. Bloomed, it disappears.',
      ],
    },
    {
      heading: 'Heat the oven and line the pan',
      body: [
        'Heat the oven to 350 °F. Grease a 9×11 pan and lay in a parchment sling — a strip up the two long sides with an inch of overhang. These are heavy and they cut best cold; the sling is how the slab comes out in one piece.',
      ],
    },
    {
      heading: 'Beat in the gluten-free flour',
      body: [
        'Whisk the baking powder and salt into the dry gluten-free flour, then work that into the bloomed TrueNooch™ Flour by hand until the colour is even. The TrueNooch™ Flour is darker and coarser and you will see it blend.',
      ],
    },
    {
      heading: 'Melt the sugar into the yogurt',
      body: [
        'Yogurt and coconut sugar together over a double boiler, 15 to 25 minutes, stirring. You are dissolving the sugar rather than just warming it — that is where the thin shiny crust on top comes from.',
        'Gentle heat, and a double boiler rather than direct: a cultured cashew base will break over a burner, and the whole reason it is in this recipe is that it brings its water bound up rather than poured in.',
      ],
    },
    {
      heading: 'Bloom the cocoa',
      body: [
        'Whisk the cocoa into the 120 g of hot water until it is a thick, glossy paste and smells like a chocolate shop. Let it sit while you finish the rest. Hot coffee instead of water works and is the classic move — just know it is a second acid in a pan that already has one, and with the sugar this low its bitterness has less to push against.',
      ],
    },
    {
      heading: 'The oil, slowly — do not break it',
      body: [
        'Off the heat, pour the oil into the sugar-and-yogurt mixture in a thin stream, stirring the whole time. Slowly is the entire instruction. This is an emulsion and fast oil splits it, and it is the hard part of this cook. Then the vanilla.',
      ],
    },
    {
      heading: 'Combine, then the chunks',
      body: [
        'Fold the bloomed cocoa into the wet. Then bring the wet and the dry together — a stand mixer is the best route for this last mix, and it is how we do it; the batter is thick by now and it is a lot to move by hand.',
        'Mix only until there are no dry streaks, scraping the bottom. Do not beat it — there is no gluten to develop and there is air you do not want.',
        'Chocolate chunks go in last, always. Scrape into the pan and push it to the corners; it is thick enough that it will not level itself.',
      ],
    },
    {
      heading: 'Bake — 45 to 90 minutes, and the spread is your pan',
      body: [
        'That range is not vagueness, it is the pan and the oven. Thin metal in convection comes in around 45 minutes. Glass in a conventional oven runs nearer 90 — glass conducts poorly, and there is a lot of water in this batter because the sugar is low and the yogurt is high.',
        'So bake by the fork, not the clock: first test at 35 minutes, then every 10 to 15. It is done when the top has gone matte and set, the edges have just pulled from the sides, and a tester comes out with a few wet crumbs on it — not clean. Clean means you went too far.',
        'Do not open the door more often than that. Every look costs a home oven 25 to 50 °F and minutes of recovery. And expect it to sit: this batter plateaus while water leaves and then sets fast at the end.',
      ],
    },
    {
      heading: 'Cool completely — this is not optional',
      body: [
        'Twenty minutes on the counter, then into the refrigerator until the slab is cold all the way through. A legume-flour crumb sets as it cools, and warm brownies out of this pan tear instead of slice.',
        'Lift the slab out by the parchment, set it on a board, and cut with a long knife wiped clean between cuts: four across, four down, sixteen.',
      ],
    },
  ],
  stats: {
    label: 'What the flour adds',
    caption:
      'Per brownie, sixteen from one 9×11 pan. The baseline is the honest starting line — a pan carrying this much cocoa and chocolate is already doing well before any TrueNooch™ goes in. Cocoa got there first; the flour is a lift, not an origin.',
    rows: [
      { label: 'Protein', base: '4.2 g', withFlour: '6.8 g', delta: '+62%' },
      { label: 'Fibre', base: '3.2 g', withFlour: '4.7 g', delta: '+47%' },
    ],
    footnote:
      'Calculated from the ingredients’ own nutrition panels and the weights above, not measured in a lab. Approximate, and not a nutrition claim — the short version is that TrueNooch™ is kind of adding a vegetable to a brownie.',
  },
  storage:
    'Covered at room temperature for up to three days, or a week in the refrigerator. They get denser and, honestly, better on the second day.',
};

export const FULL_RECIPES: Record<string, FullRecipe> = {
  [FLOUR_BROWNIES.id]: FLOUR_BROWNIES,
};

/** The one we put at the top of /recipes. */
export const FEATURED_RECIPE_ID = FLOUR_BROWNIES.id;
